// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_closet/recap
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Closet data stays local; only a DERIVED-SIGNALS
// object (per-item cost-per-wear, dormancy, category balance, season usage — see
// signals.rs) is sent for THIS recap call. Raw per-wear rows never leave the device.
//
// THREE billable actions, each ONE call (CRUD, not agentic — JSON in / text or JSON out):
//   • recap  — the on-demand narration of the computed wardrobe figures
//   • ask    — "Ask your closet" Q&A over the SAME on-device signal slice (+ the question)
//   • outfit — "Outfit ideas": 2-3 outfits composed ONLY from garments the user owns
//     (text identity: id/item/brand/category/color + an optional occasion note; photos and
//     prices NEVER leave the device — thumbnails join back client-side by id)
// NO agent loop, NO 2nd call, NO tool-use chain, NO direct vendor SDK.
//
// Compliance fence (REVISED 2026-07-02, operator-approved): outfit suggestions from the
// user's OWN logged wardrobe are ALLOWED (the outfit endpoint — composition only). Still
// banned everywhere: buy/keep/donate/declutter/resale judgments, shopping recommendations,
// and body/appearance/health commentary. recap + ask stay descriptive-only inventory math —
// "worn 2 times", never "wear it more". We compute/collect locally, call once, render.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { ClosetSignals, Garment } from "./store";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_closet";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Your Oriora balance is empty — top up to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface ClosetRecap {
  recap: string;
}

/**
 * Closet Recap — the ONE AI feature.
 *
 * We compute the wardrobe's structure on-device (per-item cost-per-wear, days dormant,
 * wears-since-purchase, category balance, season most/least worn — see store.ts /
 * signals.rs) and send ONLY those small aggregates. The brain then narrates specific,
 * named-number observations the user couldn't tally at a glance (the moat). Raw per-wear
 * rows never leave the device.
 *
 * Endpoint + Bearer JWT: POST /api/apps/oriora_closet/recap. The server prompt reads the
 * `signals` object (operator task) and stays DESCRIPTIVE ONLY — names the figures, never
 * advises (no style/buy/keep/donate/resale/body claims).
 *
 * The server is expected to return { recap: string }. The intended SYSTEM PROMPT for that
 * one call (server-side source of truth):
 *
 *   You are Oriora Closet's recap writer. You are given a JSON `signals` object of
 *   PRE-COMPUTED wardrobe inventory numbers for ONE user (cost-per-wear, wears, days
 *   dormant, category balance, season usage). Write a short, warm, plain-language recap
 *   (2-4 sentences) that NARRATES these specific numbers — name items, figures and dates
 *   from the data ("Your black boots are down to $1.40 a wear; six items haven't been
 *   touched since autumn."). Money fields are in cents; convert to the major unit.
 *   HARD RULES: describe the user's own logged data ONLY. Do the math you are given; do
 *   not invent numbers. NEVER give advice of any kind — no style tips, no buy/keep/toss/
 *   donate/declutter/resale guidance, no body or health remarks. Say "worn 2 times",
 *   never "wear it more". If there is too little data, say so plainly.
 */
export async function closetRecap(signals: ClosetSignals): Promise<ClosetRecap> {
  if (signals.overall.total_items === 0) {
    throw new BrainError("unavailable", "Add a few garments first — there's nothing to recap yet.");
  }
  const r = await brainCall<Partial<ClosetRecap>>("recap", { signals });
  return { recap: String(r.recap ?? "").trim() };
}

export interface ClosetAnswer {
  answer: string;
}

/**
 * "Ask your closet" — a repeat billable Q&A over the SAME derived signal slice plus the
 * user's question ("what's my priciest-per-wear item?", "what haven't I worn this season?").
 * ONE call, JSON-in / text-out. The server answers from the signals ONLY and stays
 * observational — it states facts the numbers support, says it can't answer when the data
 * doesn't cover it, and DECLINES any advice-shaped question (style/buy/keep/donate/resale/body).
 *
 * POST /api/apps/oriora_closet/ask  (operator task: server prompt reads `signals` + `question`).
 * Intended SYSTEM PROMPT (server-side source of truth): same descriptive-only fence as the
 * recap, answering the asked question from the pre-computed signals; refuse advice plainly.
 */
export async function askCloset(signals: ClosetSignals, question: string): Promise<ClosetAnswer> {
  const q = question.trim();
  if (!q) throw new BrainError("unavailable", "Type a question first.");
  const r = await brainCall<Partial<ClosetAnswer>>("ask", { signals, question: q });
  return { answer: String(r.answer ?? "").trim() };
}

// ── "Outfit ideas" — operator-approved styling upgrade (2026-07-02) ──────────────
export interface OutfitSuggestion {
  name: string;
  /** Garment ids from THIS device's wardrobe — server allow-lists them against what was sent. */
  garment_ids: string[];
  why: string;
}
export interface OutfitResult {
  outfits: OutfitSuggestion[];
  /** Plain remark from the brain (e.g. wardrobe too small for a full outfit). */
  note?: string;
}

/**
 * Outfit ideas — ONE billed call (POST /api/apps/oriora_closet/outfit).
 *
 * Sends ONLY the text identity of the garments the user owns (id/item/brand/category/color —
 * never photo_ref data-URLs, prices, or wear rows) plus the user's optional occasion/weather/
 * preferences note. The server composes 2-3 outfits STRICTLY from those ids (validated server-side
 * against the sent set) with a one-line why each; thumbnails are joined back to photo_ref HERE,
 * client-side — image bytes never leave the device.
 *
 * Fence (server-side source of truth): composition from the user's OWN wardrobe only — never
 * buy/keep/donate judgments, shopping recommendations, or body/appearance commentary.
 */
// ── Garment identification from a photo (parity #12) ────────────────────────
// Photo → the fields the owner would otherwise type. Returned as SUGGESTIONS: the add/edit form
// pre-fills them and the owner confirms by saving. The photo transits for this one billed call and
// is not retained; it otherwise never leaves the device.
export interface GarmentGuess {
  item: string; category: string; color: string;
  fabric: string; fit: string; brand: string;
  confidence: "high" | "medium" | "low"; note: string;
}
export async function identifyGarment(photoDataUrl: string): Promise<GarmentGuess> {
  const url = (photoDataUrl ?? "").trim();
  if (!/^data:image\//i.test(url)) throw new BrainError("unavailable", "Add a photo first.");
  const r = await brainCall<Partial<GarmentGuess>>("identify-garment", { image: url });
  // parse_failed = the server couldn't read the model's reply (Pattern 10): the READING failed,
  // the photo wasn't "unclear" — surface it instead of pre-filling an all-empty form.
  if ((r as { parse_failed?: boolean }).parse_failed) throw new BrainError("unavailable", "The reading failed — try again.");
  const s = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const c = s(r.confidence);
  return {
    item: s(r.item), category: s(r.category), color: s(r.color),
    fabric: s(r.fabric), fit: s(r.fit), brand: s(r.brand),
    confidence: c === "high" ? "high" : c === "medium" ? "medium" : "low",
    note: s(r.note),
  };
}

// ── Today's weather, fetched ON-DEVICE (parity #13) ─────────────────────────
// Open-Meteo: free, no API key, no account. The COORDINATES NEVER LEAVE THE DEVICE — only a short
// summary ("14°C now, 9–17°C today, light rain, 60% chance of rain") is passed to the brain, and
// Oriora hosts no weather data. Returns null if the customer declines location or anything fails;
// outfits then simply run without weather, exactly as before.
export interface WeatherNow {
  temp_c: number; high_c: number; low_c: number;
  rain_chance: number; wind_kph: number; summary: string;
}
const WMO: Record<number, string> = {
  0: "clear", 1: "mostly clear", 2: "partly cloudy", 3: "overcast", 45: "fog", 48: "freezing fog",
  51: "light drizzle", 53: "drizzle", 55: "heavy drizzle", 61: "light rain", 63: "rain",
  65: "heavy rain", 66: "freezing rain", 67: "freezing rain", 71: "light snow", 73: "snow",
  75: "heavy snow", 77: "snow grains", 80: "rain showers", 81: "rain showers", 82: "heavy showers",
  85: "snow showers", 86: "snow showers", 95: "thunderstorms", 96: "thunderstorms", 99: "thunderstorms",
};
export async function getWeather(): Promise<WeatherNow | null> {
  try {
    const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
      if (!navigator.geolocation) { reject(new Error("no geolocation")); return; }
      navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000, maximumAge: 900_000 });
    });
    // Round to ~1km so even the on-device request isn't precisely locating anyone.
    const lat = pos.coords.latitude.toFixed(2), lon = pos.coords.longitude.toFixed(2);
    const u = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}`
      + `&current=temperature_2m,weather_code,wind_speed_10m`
      + `&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max`
      + `&timezone=auto&forecast_days=1`;
    const res = await fetch(u);
    if (!res.ok) return null;
    const d = await res.json();
    const code = Number(d?.current?.weather_code);
    return {
      temp_c: Number(d?.current?.temperature_2m),
      high_c: Number(d?.daily?.temperature_2m_max?.[0]),
      low_c: Number(d?.daily?.temperature_2m_min?.[0]),
      rain_chance: Number(d?.daily?.precipitation_probability_max?.[0] ?? 0),
      wind_kph: Number(d?.current?.wind_speed_10m ?? 0),
      summary: WMO[code] ?? "",
    };
  } catch { return null; }
}

export async function suggestOutfits(garments: Garment[], occasion: string, weather?: WeatherNow | null): Promise<OutfitResult> {
  // Favor the most-worn garments if an (unrealistically) huge wardrobe has to be capped.
  const wardrobe = [...garments]
    .sort((a, b) => b.wear_count - a.wear_count)
    .slice(0, 400)
    .map((g) => ({ id: g.id, item: g.item, brand: g.brand, category: g.category, color: g.color }));
  if (wardrobe.length < 2) {
    throw new BrainError("unavailable", "Add at least two garments first — outfits need pieces to combine.");
  }
  const r = await brainCall<Partial<OutfitResult>>("outfit", {
    wardrobe, occasion: occasion.trim(),
    ...(weather ? { weather } : {}),   // omitted entirely when unavailable/declined
  });
  const outfits = (Array.isArray(r.outfits) ? r.outfits : [])
    .map((o) => ({
      name: String(o?.name ?? "").trim() || "Outfit",
      garment_ids: Array.isArray(o?.garment_ids) ? o.garment_ids.map(String) : [],
      why: String(o?.why ?? "").trim(),
    }))
    .filter((o) => o.garment_ids.length > 0);
  const note = typeof r.note === "string" && r.note.trim() ? r.note.trim() : undefined;
  return { outfits, note };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_closet/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile (mirrors expense_audit_ai). Session returns via auth:deep-link (App.tsx + lib.rs).
// The web callback must allowlist `oriora-closet` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");
